import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { exchangeOAuthCode } from '../lib/api/auth';

export default function AuthCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { refreshUser } = useAuth();
    // The handoff code is SINGLE-USE server-side. This effect re-ran after a
    // successful exchange (refreshUser() updates auth state → the context
    // re-renders → a new refreshUser identity → effect fires again), which
    // redeemed the already-consumed code, got a 400, and bounced every
    // Google sign-in to /login?error=auth_failed right after it had in
    // fact succeeded. Remember which code we've handled so a re-run is a
    // no-op instead of a second redemption.
    const handledCode = useRef(null);

    useEffect(() => {
        const processCallback = async () => {
            const code = searchParams.get('code');
            const error = searchParams.get('error');

            if (error) {
                navigate('/login?error=' + error);
                return;
            }

            if (code) {
                if (handledCode.current === code) return;
                handledCode.current = code;
                try {
                    // The redirect carries only a one-time code, never the
                    // real tokens (see auth.js's exchangeOAuthCode). The
                    // exchange response sets the HttpOnly auth cookies
                    // server-side (issue #196) — nothing for this JS to
                    // store; refreshUser() below picks up the new session
                    // via the cookie the browser just received.
                    await exchangeOAuthCode(code);
                    await refreshUser();
                    navigate('/generator');
                } catch (err) {
                    console.error('Callback error:', err);
                    navigate('/login?error=auth_failed');
                }
            } else {
                navigate('/login');
            }
        };

        processCallback();
    }, [searchParams, navigate, refreshUser]);

    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Authenticating...</div>;
}
